import _ from "lodash";

import toast from "components/Toast";

import { ETransactionType } from "entities/Deal/constants";
import { IMoneyField, ITransaction } from "entities/Deal/sdk";
import { ITransactionValuesForm } from "entities/Deal/sdk";

import {
  DEBT_TRANSACTION_EMPTY_VALUES,
  EQUITY_TRANSACTION_EMPTY_VALUES,
  GRANT_TRANSACTION_EMPTY_VALUES,
  OTHER_TRANSACTION_EMPTY_VALUES,
  TAX_CREDIT_ALLOCATION_TRANSACTION_EMPTY_VALUES,
} from "./constants";

const getMoneyFieldAmountOrNull = ({ field }: { field: IMoneyField | null }) =>
  !_.isNil(field) ? field.amount : null;

export const getTransactionTypeAndStatusFromTransaction = ({
  transaction,
}: {
  transaction: ITransaction;
}) => {
  if (!_.isNil(transaction.equity_transaction)) {
    return {
      transaction_type: ETransactionType.EQUITY_TRANSACTION,
      status: transaction.equity_transaction.status,
    };
  }
  if (!_.isNil(transaction.debt_transaction)) {
    return {
      transaction_type: ETransactionType.DEBT_TRANSACTION,
      status: transaction.debt_transaction.status,
    };
  }
  if (!_.isNil(transaction.tax_credit_allocation_transaction)) {
    return {
      transaction_type: ETransactionType.TAX_CREDIT_ALLOCATION_TRANSACTION,
      status: transaction.tax_credit_allocation_transaction.status,
    };
  }
  if (!_.isNil(transaction.grant_transaction)) {
    return {
      transaction_type: ETransactionType.GRANT_TRANSACTION,
      status: transaction.grant_transaction.status,
    };
  }
  if (!_.isNil(transaction.other_transaction)) {
    return {
      transaction_type: ETransactionType.OTHER_TRANSACTION,
      status: transaction.other_transaction.status,
    };
  }
  return { transaction_type: "", status: null };
};

export const extractInitialValuesFromTransaction = ({
  transaction,
}: {
  transaction: ITransaction;
}): ITransactionValuesForm => {
  const { transaction_type, status } =
    getTransactionTypeAndStatusFromTransaction({
      transaction,
    });

  const financingProgramId = !_.isNil(transaction.financing_program?.id)
    ? transaction.financing_program?.id
    : null;
  const financingProgramName = !_.isNil(transaction.financing_program?.name)
    ? transaction.financing_program?.name
    : null;

  const equityTransaction = transaction.equity_transaction
    ? {
        ...transaction.equity_transaction,
        housing_credit_equity: getMoneyFieldAmountOrNull({
          field: transaction.equity_transaction.housing_credit_equity,
        }),
        other_sources_amount: getMoneyFieldAmountOrNull({
          field: transaction.equity_transaction.other_sources_amount,
        }),
      }
    : null;

  const debtTransaction = !_.isNil(transaction.debt_transaction)
    ? {
        ...transaction.debt_transaction,
        other_sources_amount: getMoneyFieldAmountOrNull({
          field: transaction.debt_transaction.other_sources_amount,
        }),
      }
    : null;

  const taxCreditAllocationTransaction = !_.isNil(
    transaction.tax_credit_allocation_transaction
  )
    ? {
        ...transaction.tax_credit_allocation_transaction,
        credit_amount_10_year: getMoneyFieldAmountOrNull({
          field:
            transaction.tax_credit_allocation_transaction.credit_amount_10_year,
        }),
        housing_credit_equity: getMoneyFieldAmountOrNull({
          field:
            transaction.tax_credit_allocation_transaction.housing_credit_equity,
        }),
        equity_amount: getMoneyFieldAmountOrNull({
          field: transaction.tax_credit_allocation_transaction.equity_amount,
        }),
        price_per_credit: getMoneyFieldAmountOrNull({
          field: transaction.tax_credit_allocation_transaction.price_per_credit,
        }),
      }
    : null;

  const grantTransaction = !_.isNil(transaction.grant_transaction)
    ? {
        ...transaction.grant_transaction,
        other_sources_amount: getMoneyFieldAmountOrNull({
          field: transaction.grant_transaction.other_sources_amount,
        }),
      }
    : null;

  const otherTransaction = !_.isNil(transaction.other_transaction)
    ? {
        ...transaction.other_transaction,
        other_sources_amount: getMoneyFieldAmountOrNull({
          field: transaction.other_transaction.other_sources_amount,
        }),
      }
    : null;

  return {
    capital_provider: transaction.capital_provider,
    description: transaction.description,
    transaction_type,
    financing_program_id: financingProgramId,
    financing_program_name: financingProgramName,
    status,
    amount: transaction.amount.amount,
    equity_transaction: equityTransaction,
    debt_transaction: debtTransaction,
    tax_credit_allocation_transaction: taxCreditAllocationTransaction,
    grant_transaction: grantTransaction,
    other_transaction: otherTransaction,
  };
};

export const handleTransactionInvalidRequest = (
  error:
    | {
        [key: string]: Array<string>;
      }
    | null
    | undefined
) => {
  if (_.isNil(error)) {
    return;
  }

  let errorMessage: string | Array<string> = error?.message || error?.detail;

  if (_.isUndefined(errorMessage) && (_.isString(error) || _.isArray(error))) {
    errorMessage = error;
  }

  if (_.isString(errorMessage)) {
    toast.errorMessage(errorMessage);
  } else if (_.isArray(errorMessage)) {
    _.map(errorMessage, (fieldError: string) => toast.errorMessage(fieldError));
  } else {
    Object.entries(error).forEach(([key, fieldErrors]) => {
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach((fieldError) => {
          if (typeof fieldError === "string") {
            // TODO Show fieldErrors in the form instead of in toasts when
            toast.errorMessage(
              `Error found in ${key.replace("_", " ")} field: \n ${fieldError}`
            );
          } else {
            handleTransactionInvalidRequest(fieldError);
          }
        });
      }
    });
  }
};

export const handleTransactionTypeChange = ({
  transactionType,
  setFieldValue,
}: {
  transactionType: ETransactionType;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}) => {
  setFieldValue("debt_transaction", null);
  setFieldValue("tax_credit_allocation_transaction", null);
  setFieldValue("grant_transaction", null);
  setFieldValue("other_transaction", null);
  setFieldValue("equity_transaction", null);

  if (transactionType === ETransactionType.EQUITY_TRANSACTION) {
    setFieldValue("equity_transaction", EQUITY_TRANSACTION_EMPTY_VALUES);
  } else if (transactionType === ETransactionType.DEBT_TRANSACTION) {
    setFieldValue("debt_transaction", DEBT_TRANSACTION_EMPTY_VALUES);
  } else if (
    transactionType === ETransactionType.TAX_CREDIT_ALLOCATION_TRANSACTION
  ) {
    setFieldValue(
      "tax_credit_allocation_transaction",
      TAX_CREDIT_ALLOCATION_TRANSACTION_EMPTY_VALUES
    );
  } else if (transactionType === ETransactionType.GRANT_TRANSACTION) {
    setFieldValue("grant_transaction", GRANT_TRANSACTION_EMPTY_VALUES);
  } else if (transactionType === ETransactionType.OTHER_TRANSACTION) {
    setFieldValue("other_transaction", OTHER_TRANSACTION_EMPTY_VALUES);
  }
};
